/**
 * Central configuration schema - single source of truth for all config fields
 *
 * This file defines:
 * - Default values for all configuration fields
 * - Which fields should be tracked for "unsaved changes" detection
 * - TypeScript types derived from the schema
 *
 * When adding a new config field:
 * 1. Add it to CONFIG_FIELDS with its default value and trackChanges setting
 * 2. Add it to DEFAULT_CONFIG (TypeScript will enforce this)
 * 3. ConfigState type and TRACKABLE_CONFIG_KEYS are automatically derived
 */

import { SponsorBlockCategories } from '../components/Configuration/types';

/**
 * Configuration field registry
 * Each field defines its default value and whether changes should be tracked
 */
export const CONFIG_FIELDS = {
  // Channel settings
  // Defaults must match config/config.example.json: useConfig fills missing
  // server fields from here and save POSTs the full object back.
  // Enforced by configSchemaAlignment.test.ts.
  channelAutoDownload: { default: false, trackChanges: true },
  channelDownloadFrequency: { default: '0 * * * *', trackChanges: true },
  channelFilesToDownload: { default: 5, trackChanges: true },

  // Video settings
  preferredResolution: { default: '1080', trackChanges: true },
  videoCodec: { default: 'default', trackChanges: true },
  defaultSubfolder: { default: '', trackChanges: true },
  defaultSkipVideoFolder: { default: false, trackChanges: true },
  videoFilenamePrefix: {
    default: '%(uploader,channel,uploader_id).80B - %(title).64B',
    trackChanges: true,
  },

  // Plex integration
  plexApiKey: { default: '', trackChanges: true },
  plexYoutubeLibraryId: { default: '', trackChanges: true },
  plexSubfolderLibraryMappings: {
    default: [] as Array<{ subfolder: string | null; libraryId: string }>,
    trackChanges: true,
  },
  plexIP: { default: '', trackChanges: true },
  plexPort: { default: '32400', trackChanges: true },
  plexViaHttps: { default: false, trackChanges: true },
  plexPlaylistToken: { default: '', trackChanges: true },

  // Jellyfin integration
  jellyfinEnabled: { default: false, trackChanges: true },
  jellyfinUrl: { default: '', trackChanges: true },
  jellyfinApiKey: { default: '', trackChanges: true },
  jellyfinUserId: { default: '', trackChanges: true },
  jellyfinVideoLibraryIds: { default: [] as string[], trackChanges: true },

  // Emby integration
  embyEnabled: { default: false, trackChanges: true },
  embyUrl: { default: '', trackChanges: true },
  embyApiKey: { default: '', trackChanges: true },
  embyUserId: { default: '', trackChanges: true },
  embyVideoLibraryIds: { default: [] as string[], trackChanges: true },

  // Media server watch status sync
  watchStatusSyncEnabled: { default: true, trackChanges: true },
  watchStatusSyncFrequency: { default: '0 */4 * * *', trackChanges: true },
  plexWatchStatusAllUsers: { default: true, trackChanges: true },
  jellyfinWatchStatusAllUsers: { default: true, trackChanges: true },
  embyWatchStatusAllUsers: { default: true, trackChanges: true },
  watchStatusWatchedRule: { default: 'any' as 'any' | 'primary', trackChanges: true },

  // YouTube Data API
  youtubeApiKey: { default: '', trackChanges: true },

  // SponsorBlock
  sponsorblockEnabled: { default: false, trackChanges: true },
  sponsorblockAction: { default: 'remove' as 'remove' | 'mark', trackChanges: true },
  sponsorblockCategories: {
    default: {
      sponsor: true,
      intro: false,
      outro: false,
      selfpromo: true,
      preview: false,
      filler: false,
      interaction: false,
      music_offtopic: false,
    } as SponsorBlockCategories,
    trackChanges: true
  },
  sponsorblockApiUrl: { default: '', trackChanges: true },

  // Download performance
  downloadSocketTimeoutSeconds: { default: 30, trackChanges: true },
  downloadThrottledRate: { default: '100K', trackChanges: true },
  downloadRetryCount: { default: 2, trackChanges: true },
  downloadAutoRetryCount: { default: 1, trackChanges: true },
  enableStallDetection: { default: true, trackChanges: true },
  stallDetectionWindowSeconds: { default: 30, trackChanges: true },
  stallDetectionRateThreshold: { default: '100K', trackChanges: true },

  // Advanced settings
  sleepRequests: { default: 1, trackChanges: true },
  proxy: { default: '', trackChanges: true },

  // Cookies
  cookiesEnabled: { default: false, trackChanges: true },
  customCookiesUploaded: { default: false, trackChanges: true },

  // Kodi compatibility
  writeChannelPosters: { default: true, trackChanges: true },
  writeVideoNfoFiles: { default: true, trackChanges: true },
  writeVideoFanart: { default: false, trackChanges: true },
  writeBackdropImages: { default: false, trackChanges: true },

  // Notifications
  notificationsEnabled: { default: false, trackChanges: true },
  appriseUrls: { default: [] as Array<{ url: string; name: string; richFormatting?: boolean }>, trackChanges: true },

  // Auto removal
  autoRemovalEnabled: { default: false, trackChanges: true },
  autoRemovalFreeSpaceThreshold: { default: '', trackChanges: true },
  autoRemovalVideoAgeThreshold: { default: '', trackChanges: true },
  autoRemovalWatchedEnabled: { default: false, trackChanges: true },
  autoRemovalWatchedMinDaysSinceWatched: { default: '', trackChanges: true },
  autoRemovalWatchedMinVideoAgeDays: { default: '', trackChanges: true },
  autoRemovalKeepRecentCount: { default: 0, trackChanges: true },

  // Storage
  useTmpForDownloads: { default: false, trackChanges: true },
  tmpFilePath: { default: '/tmp/youtarr-downloads', trackChanges: false }, // Not tracked for changes

  // Subtitles
  subtitlesEnabled: { default: false, trackChanges: true },
  subtitleLanguage: { default: 'en', trackChanges: true },

  // Appearance
  darkModeEnabled: { default: false, trackChanges: true },
  channelVideosHotLoad: { default: false, trackChanges: true },

  // API Keys
  apiKeyRateLimit: { default: 10, trackChanges: true },

  // yt-dlp auto-update
  autoUpdateYtdlp: { default: false, trackChanges: true },
  ytdlpUpdateChannel: { default: 'stable' as 'stable' | 'nightly', trackChanges: true },
  ytdlpLastChecked: { default: null as string | null, trackChanges: false },
  ytdlpLastUpdated: { default: null as string | null, trackChanges: false },
  ytdlpLastResult: {
    default: null as { status: 'updated' | 'up-to-date' | 'skipped' | 'error'; message?: string; version?: string } | null,
    trackChanges: false,
  },
  rescanLastRun: {
    default: null as {
      startedAt: string;
      completedAt: string;
      trigger: 'manual' | 'scheduled' | 'startup';
      status: 'completed' | 'timed-out' | 'error';
      videosUpdated: number;
      videosMarkedMissing: number;
      videosScanned: number;
      filesFoundOnDisk: number;
      errorMessage: string | null;
    } | null,
    trackChanges: false,
  },

  // yt-dlp options (custom args, IP family, rate limit)
  ytdlpIpFamily: { default: 'ipv4' as 'ipv4' | 'ipv6' | 'auto', trackChanges: true },
  ytdlpDownloadRateLimit: { default: '', trackChanges: true },
  ytdlpCustomArgs: { default: '', trackChanges: true },

  // System/internal fields (not tracked for changes)
  youtubeOutputDirectory: { default: '', trackChanges: false },
  uuid: { default: '', trackChanges: false },
  envAuthApplied: { default: false, trackChanges: false },
};

/**
 * Derived ConfigState type from the schema
 * This ensures type safety and automatic inference of field types
 */
export type ConfigState = {
  [K in keyof typeof CONFIG_FIELDS]: (typeof CONFIG_FIELDS)[K]['default']
};

/**
 * Default configuration object
 * Automatically generated from CONFIG_FIELDS
 */
export const DEFAULT_CONFIG: ConfigState = {
  channelAutoDownload: CONFIG_FIELDS.channelAutoDownload.default,
  channelDownloadFrequency: CONFIG_FIELDS.channelDownloadFrequency.default,
  channelFilesToDownload: CONFIG_FIELDS.channelFilesToDownload.default,
  preferredResolution: CONFIG_FIELDS.preferredResolution.default,
  videoCodec: CONFIG_FIELDS.videoCodec.default,
  defaultSubfolder: CONFIG_FIELDS.defaultSubfolder.default,
  defaultSkipVideoFolder: CONFIG_FIELDS.defaultSkipVideoFolder.default,
  videoFilenamePrefix: CONFIG_FIELDS.videoFilenamePrefix.default,
  plexApiKey: CONFIG_FIELDS.plexApiKey.default,
  plexYoutubeLibraryId: CONFIG_FIELDS.plexYoutubeLibraryId.default,
  plexSubfolderLibraryMappings: CONFIG_FIELDS.plexSubfolderLibraryMappings.default,
  plexIP: CONFIG_FIELDS.plexIP.default,
  plexPort: CONFIG_FIELDS.plexPort.default,
  plexViaHttps: CONFIG_FIELDS.plexViaHttps.default,
  plexPlaylistToken: CONFIG_FIELDS.plexPlaylistToken.default,
  jellyfinEnabled: CONFIG_FIELDS.jellyfinEnabled.default,
  jellyfinUrl: CONFIG_FIELDS.jellyfinUrl.default,
  jellyfinApiKey: CONFIG_FIELDS.jellyfinApiKey.default,
  jellyfinUserId: CONFIG_FIELDS.jellyfinUserId.default,
  jellyfinVideoLibraryIds: CONFIG_FIELDS.jellyfinVideoLibraryIds.default,
  embyEnabled: CONFIG_FIELDS.embyEnabled.default,
  embyUrl: CONFIG_FIELDS.embyUrl.default,
  embyApiKey: CONFIG_FIELDS.embyApiKey.default,
  embyUserId: CONFIG_FIELDS.embyUserId.default,
  embyVideoLibraryIds: CONFIG_FIELDS.embyVideoLibraryIds.default,
  watchStatusSyncEnabled: CONFIG_FIELDS.watchStatusSyncEnabled.default,
  watchStatusSyncFrequency: CONFIG_FIELDS.watchStatusSyncFrequency.default,
  plexWatchStatusAllUsers: CONFIG_FIELDS.plexWatchStatusAllUsers.default,
  jellyfinWatchStatusAllUsers: CONFIG_FIELDS.jellyfinWatchStatusAllUsers.default,
  embyWatchStatusAllUsers: CONFIG_FIELDS.embyWatchStatusAllUsers.default,
  watchStatusWatchedRule: CONFIG_FIELDS.watchStatusWatchedRule.default,
  youtubeApiKey: CONFIG_FIELDS.youtubeApiKey.default,
  sponsorblockEnabled: CONFIG_FIELDS.sponsorblockEnabled.default,
  sponsorblockAction: CONFIG_FIELDS.sponsorblockAction.default,
  sponsorblockCategories: CONFIG_FIELDS.sponsorblockCategories.default,
  sponsorblockApiUrl: CONFIG_FIELDS.sponsorblockApiUrl.default,
  downloadSocketTimeoutSeconds: CONFIG_FIELDS.downloadSocketTimeoutSeconds.default,
  downloadThrottledRate: CONFIG_FIELDS.downloadThrottledRate.default,
  downloadRetryCount: CONFIG_FIELDS.downloadRetryCount.default,
  downloadAutoRetryCount: CONFIG_FIELDS.downloadAutoRetryCount.default,
  enableStallDetection: CONFIG_FIELDS.enableStallDetection.default,
  stallDetectionWindowSeconds: CONFIG_FIELDS.stallDetectionWindowSeconds.default,
  stallDetectionRateThreshold: CONFIG_FIELDS.stallDetectionRateThreshold.default,
  sleepRequests: CONFIG_FIELDS.sleepRequests.default,
  proxy: CONFIG_FIELDS.proxy.default,
  cookiesEnabled: CONFIG_FIELDS.cookiesEnabled.default,
  customCookiesUploaded: CONFIG_FIELDS.customCookiesUploaded.default,
  writeChannelPosters: CONFIG_FIELDS.writeChannelPosters.default,
  writeVideoNfoFiles: CONFIG_FIELDS.writeVideoNfoFiles.default,
  writeVideoFanart: CONFIG_FIELDS.writeVideoFanart.default,
  writeBackdropImages: CONFIG_FIELDS.writeBackdropImages.default,
  notificationsEnabled: CONFIG_FIELDS.notificationsEnabled.default,
  appriseUrls: CONFIG_FIELDS.appriseUrls.default,
  autoRemovalEnabled: CONFIG_FIELDS.autoRemovalEnabled.default,
  autoRemovalFreeSpaceThreshold: CONFIG_FIELDS.autoRemovalFreeSpaceThreshold.default,
  autoRemovalVideoAgeThreshold: CONFIG_FIELDS.autoRemovalVideoAgeThreshold.default,
  autoRemovalWatchedEnabled: CONFIG_FIELDS.autoRemovalWatchedEnabled.default,
  autoRemovalWatchedMinDaysSinceWatched: CONFIG_FIELDS.autoRemovalWatchedMinDaysSinceWatched.default,
  autoRemovalWatchedMinVideoAgeDays: CONFIG_FIELDS.autoRemovalWatchedMinVideoAgeDays.default,
  autoRemovalKeepRecentCount: CONFIG_FIELDS.autoRemovalKeepRecentCount.default,
  useTmpForDownloads: CONFIG_FIELDS.useTmpForDownloads.default,
  tmpFilePath: CONFIG_FIELDS.tmpFilePath.default,
  subtitlesEnabled: CONFIG_FIELDS.subtitlesEnabled.default,
  subtitleLanguage: CONFIG_FIELDS.subtitleLanguage.default,
  darkModeEnabled: CONFIG_FIELDS.darkModeEnabled.default,
  channelVideosHotLoad: CONFIG_FIELDS.channelVideosHotLoad.default,
  apiKeyRateLimit: CONFIG_FIELDS.apiKeyRateLimit.default,
  autoUpdateYtdlp: CONFIG_FIELDS.autoUpdateYtdlp.default,
  ytdlpUpdateChannel: CONFIG_FIELDS.ytdlpUpdateChannel.default,
  ytdlpLastChecked: CONFIG_FIELDS.ytdlpLastChecked.default,
  ytdlpLastUpdated: CONFIG_FIELDS.ytdlpLastUpdated.default,
  ytdlpLastResult: CONFIG_FIELDS.ytdlpLastResult.default,
  rescanLastRun: CONFIG_FIELDS.rescanLastRun.default,
  ytdlpIpFamily: CONFIG_FIELDS.ytdlpIpFamily.default,
  ytdlpDownloadRateLimit: CONFIG_FIELDS.ytdlpDownloadRateLimit.default,
  ytdlpCustomArgs: CONFIG_FIELDS.ytdlpCustomArgs.default,
  youtubeOutputDirectory: CONFIG_FIELDS.youtubeOutputDirectory.default,
  uuid: CONFIG_FIELDS.uuid.default,
  envAuthApplied: CONFIG_FIELDS.envAuthApplied.default,
};

/**
 * Array of config keys that should be tracked for unsaved changes
 * Automatically filtered from CONFIG_FIELDS where trackChanges is true
 */
export const TRACKABLE_CONFIG_KEYS = Object.entries(CONFIG_FIELDS)
  .filter(([_, meta]) => meta.trackChanges)
  .map(([key, _]) => key) as (keyof ConfigState)[];
